import type { ResponseInterceptor } from "@/app/api";
import { EnumBillingPlanStatus } from "@/app/services/generated/enums/enumBillingPlanStatus.generated";
import { EnumStockLevelDisplay } from "@/app/services/generated/enums/enumStockLevelDisplay.generated";
import type { EnumTpPaymentMethod } from "@/app/services/generated/enums/enumTpPaymentMethod.generated";
import { EnumTpProductListSortOption } from "@/app/services/generated/enums/enumTpProductListSortOption.generated";
import type {
  TpOutlet,
  TpTenant,
} from "@/app/services/generated/tradeportalService";
import type Guid from "@/app/types/common/guid";

export const formatTpStoreId = (storeCode: string, tenantId: Guid) =>
  (storeCode + "_" + tenantId.toString().substring(0, 8)).toLocaleLowerCase();

const emptyTenant = {
  storeName: "",
  storeCode: "",
  tenantId: null as Guid | null,
  telephoneNumber: "",
  emailAddress: "",
  website: "",
  address: "",
  currencySymbolPrefix: "",
  currencyMaxDecimalPlaces: 2,
  currencySymbolSuffix: "",
  stripePublicApiKey: "",
  stockLevelDisplay: EnumStockLevelDisplay.off,
  lowStockThreshold: null as number | null,
  paymentMethods: [] as readonly EnumTpPaymentMethod[],
  billingStatus: EnumBillingPlanStatus.trialling,
  termsAndConditionsLink: "",
  minimumOrderValue: null as number | null,
  defaultProductSortOption: EnumTpProductListSortOption.productNameAlphabetical,
};

export class TpSessionData {
  private _sessionToken = "";
  private _tenant: TpTenant | null = null;
  private _isMorphedUser = false;
  private _tpOutlet: TpOutlet | null = null;
  public responseInterceptors: ResponseInterceptor[] = [];

  public get sessionToken() {
    return this._sessionToken;
  }

  public get tenant() {
    return this._tenant;
  }

  public get tpOutlet() {
    return this._tpOutlet;
  }

  public setSession(sessionToken: string) {
    this._sessionToken = sessionToken;
  }

  public setStore(tenant: TpTenant | null) {
    this._tenant = tenant;
  }

  public setOutlet(tpOutlet: TpOutlet | null) {
    this._tpOutlet = tpOutlet;
  }

  public get tenantOrDefault(): typeof emptyTenant {
    return this._tenant ?? emptyTenant;
  }

  public get isMorphedUser() {
    return this._isMorphedUser;
  }

  public setIsMorphedUser(isMorphedUser: boolean) {
    this._isMorphedUser = isMorphedUser;
  }

  public get minimumOrderValue() {
    return (
      this._tpOutlet?.minimumOrderValue ?? this._tenant?.minimumOrderValue ?? 0
    );
  }

  public get showPrices() {
    return this._tpOutlet?.showPrices ?? false;
  }

  public allowedPaymentMethods() {
    return this.tenantOrDefault.paymentMethods.filter(
      (x) =>
        (x.isAccount() && this._tpOutlet?.payOnAccountAllowed) ||
        (x.isStripe() && this._tpOutlet?.showPrices)
    );
  }

  public currentStoreId() {
    return this._tenant
      ? formatTpStoreId(this._tenant.storeCode, this._tenant.tenantId)
      : "";
  }
}

const tpSessionData = new TpSessionData();

export { tpSessionData };
